import React from 'react'
import { J1DownarrowD } from '@e3dc-react/icons'
import { AccordionSummary, Box, Stack, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useTypedSelector } from '../../State/RootReducer'
import { AppName } from '../../State/App/AppModel'
import { Typography } from '../Typography'

interface StyleProps {
  app?: AppName
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  headerContent: {
    width: '100%',
    alignItems: 'center',
    flexDirection: 'row',
    color: theme.palette.accordion.text,
    overflow: 'hidden',
    paddingLeft: theme.spacing(4),
    '& .MuiAccordionSummary-content': {
      margin: 0,
      width: '100%',
      minHeight: 42,
      justifyContent: 'space-between',
      marginRight: theme.spacing(2),
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      paddingTop: 12,
      paddingBottom: 12,
      pr: 4,
    },
    '& svg': {
      fontSize: theme.shape.accordion.arrowSize ?? 40,
    },

    [theme.breakpoints.down('sm')]: {
      '& .MuiAccordionSummary-content': {
        width: '50%',
      },
    },
  },
  text: {
    fontSize: theme.typography.accordionTitle.fontSize,
    color: theme.palette.accordion.title,
    fontWeight: theme.typography.accordionTitle.fontWeight,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.body1.fontSize,
    },
  },
  disableClick: {
    cursor: 'default',
  },
  info: {
    display: 'flex',
    alignItems: 'center',
  },
}))

export interface AccordionHeaderProps {
  title: string | React.ReactNode
  hideIcon: boolean
  expandOnlyOnIcon?: boolean
  onClick: () => void
  headerContentClassName?: string
  isExpanded?: boolean
  children?: React.ReactNode
  icon?: React.ReactNode
  applyDefaultChildClasses?: boolean
  customWidth?: string | number
}

const AccordionHeader: React.FC<AccordionHeaderProps> = ({
  title,
  children,
  hideIcon,
  expandOnlyOnIcon,
  onClick,
  headerContentClassName,
  isExpanded,
  icon,
  applyDefaultChildClasses = true,
  customWidth,
}) => {
  const app = useTypedSelector(state => state.app.app)
  const { classes, cx } = useStyles({ app })
  const theme = useTheme()

  return (
    <AccordionSummary
      expandIcon={
        hideIcon ? undefined : (
          <J1DownarrowD
            onClick={event => {
              if (expandOnlyOnIcon) {
                event.stopPropagation()
                event.preventDefault()
                onClick()
              }
            }}
          />
        )
      }
      className={cx(classes.headerContent, headerContentClassName)}
      classes={{
        content: expandOnlyOnIcon ? classes.disableClick : undefined,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" width={customWidth ?? '90%'}>
        {typeof title === 'string' ? (
          <Typography noWrap variant={theme.typography.accordionTitle.variant} className={classes.text}>
            {title}
          </Typography>
        ) : (
          title
        )}
        {icon && <Box sx={{ margin: `auto auto auto ${theme.spacing(2)}` }}>{icon}</Box>}
      </Stack>
      <Box
        onClick={event => {
          if (expandOnlyOnIcon) event?.stopPropagation()
        }}
        className={cx([{ [classes.disableClick]: expandOnlyOnIcon, [classes.info]: applyDefaultChildClasses }])}
      >
        {children}
      </Box>
    </AccordionSummary>
  )
}

export default AccordionHeader
